import React from "react";
import {
  EVENTOS_DE_ACTIVIDAD,
  minutosDeGracia,
  msRestantes,
  olvidarActividad,
  registrarActividad,
  sesionInactiva,
  ultimaActividad,
} from "services/inactividadSesion";

// Cierra la sesión cuando nadie la usa.
//
// Los 20 minutos configurados eran la vida del access token, no de la sesión:
// con el refresh de 7 días y la renovación silenciosa, la sesión se renovaba
// sola indefinidamente. Esto le pone el límite que se esperaba.
//
// Hay dos guardianes y los dos hacen falta:
//   - este temporizador cierra la pantalla aunque nadie toque nada;
//   - el bloqueo en httpClient impide que una petición rezagada la reviva.
// Con uno solo, una pestaña olvidada en segundo plano seguiría renovando.

/** Escribir en localStorage en cada tecla es ruido; basta con esta frecuencia. */
const MS_ENTRE_MARCAS = 15_000;

export default function useCierrePorInactividad(activo, cerrarSesion) {
  const cerrarRef = React.useRef(cerrarSesion);
  cerrarRef.current = cerrarSesion;

  React.useEffect(() => {
    if (!activo) {
      olvidarActividad();
      return undefined;
    }

    // Al entrar se arranca el reloj. Sin esto, una sesión restaurada desde
    // disco no tendría marca y nunca caducaría.
    if (!ultimaActividad()) registrarActividad();

    let temporizador = null;
    let ultimaMarca = 0;
    let terminado = false;

    const cerrarPorInactividad = () => {
      if (terminado) return;
      terminado = true;
      olvidarActividad();
      Promise.resolve(cerrarRef.current?.()).catch(() => {});
    };

    const reprogramar = () => {
      if (terminado) return;
      if (temporizador) clearTimeout(temporizador);
      const restante = msRestantes();
      if (restante <= 0) {
        cerrarPorInactividad();
        return;
      }
      // Se despierta justo cuando toca. Si el equipo estuvo suspendido el
      // temporizador llega tarde, y por eso igual se revisa al volver.
      temporizador = setTimeout(reprogramar, restante + 250);
    };

    const alHaberActividad = () => {
      if (terminado) return;
      // Si ya se pasó el plazo, un clic no revive la sesión: la cierra.
      if (sesionInactiva()) {
        cerrarPorInactividad();
        return;
      }
      const ahora = Date.now();
      if (ahora - ultimaMarca < MS_ENTRE_MARCAS) return;
      ultimaMarca = ahora;
      registrarActividad(ahora);
      reprogramar();
    };

    const alVolverAlFrente = () => {
      if (document.visibilityState !== "visible") return;
      // Con la pestaña oculta o el computador suspendido los temporizadores se
      // atrasan o se congelan. Acá se compara contra el reloj real.
      if (sesionInactiva()) cerrarPorInactividad();
      else reprogramar();
    };

    for (const evento of EVENTOS_DE_ACTIVIDAD) {
      window.addEventListener(evento, alHaberActividad, { passive: true });
    }
    document.addEventListener("visibilitychange", alVolverAlFrente);
    window.addEventListener("focus", alVolverAlFrente);
    reprogramar();

    return () => {
      terminado = true;
      if (temporizador) clearTimeout(temporizador);
      for (const evento of EVENTOS_DE_ACTIVIDAD) {
        window.removeEventListener(evento, alHaberActividad);
      }
      document.removeEventListener("visibilitychange", alVolverAlFrente);
      window.removeEventListener("focus", alVolverAlFrente);
    };
  }, [activo]);
}

export { minutosDeGracia };
